/**
 * ZoomManager Module
 * Handles zoom and pan functionality with pinch, scroll, and double-tap support
 */

export interface ZoomState {
  scale: number;
  offsetX: number;
  offsetY: number;
  isPinching: boolean;
  isPanning: boolean;
}

export interface ZoomConfig {
  minScale: number;
  maxScale: number;
  zoomStep: number;
  doubleTapScale: number;
  animationDuration: number;
  onZoomChange?: (scale: number) => void;
  onModeChange?: (mode: 'rotate' | 'pan') => void;
}

export class ZoomManager {
  private state: ZoomState;
  private config: ZoomConfig;
  private element: HTMLElement | null = null;
  private animationId: number | null = null;

  // Pinch tracking
  private lastPinchDistance: number = 0;

  // Pan tracking
  private panStartX: number = 0;
  private panStartY: number = 0;
  private panStartOffsetX: number = 0;
  private panStartOffsetY: number = 0;

  // Double-tap detection
  private lastTapTime: number = 0;
  private lastTapX: number = 0;
  private lastTapY: number = 0;
  private readonly DOUBLE_TAP_DELAY = 300;
  private readonly DOUBLE_TAP_DISTANCE = 30;

  constructor(config: Partial<ZoomConfig> = {}) {
    this.config = {
      minScale: 1,
      maxScale: 3,
      zoomStep: 0.25,
      doubleTapScale: 2,
      animationDuration: 200,
      ...config,
    };

    this.state = {
      scale: 1,
      offsetX: 0,
      offsetY: 0,
      isPinching: false,
      isPanning: false,
    };

    // Bind methods
    this.handleWheel = this.handleWheel.bind(this);
    this.handleTouchStart = this.handleTouchStart.bind(this);
    this.handleTouchMove = this.handleTouchMove.bind(this);
    this.handleTouchEnd = this.handleTouchEnd.bind(this);
    this.handleMouseDown = this.handleMouseDown.bind(this);
    this.handleMouseMove = this.handleMouseMove.bind(this);
    this.handleMouseUp = this.handleMouseUp.bind(this);
    this.handleDoubleClick = this.handleDoubleClick.bind(this);
  }

  /**
   * Attach event listeners to element
   */
  attach(element: HTMLElement): void {
    this.element = element;

    // Wheel zoom (desktop)
    element.addEventListener('wheel', this.handleWheel, { passive: false });

    // Double-click zoom (desktop)
    element.addEventListener('dblclick', this.handleDoubleClick);

    // Touch events (mobile)
    element.addEventListener('touchstart', this.handleTouchStart, { passive: false });
    document.addEventListener('touchmove', this.handleTouchMove, { passive: false });
    document.addEventListener('touchend', this.handleTouchEnd);

    // Mouse pan (desktop, when zoomed)
    element.addEventListener('mousedown', this.handleMouseDown);
    document.addEventListener('mousemove', this.handleMouseMove);
    document.addEventListener('mouseup', this.handleMouseUp);
  }

  /**
   * Detach event listeners
   */
  detach(): void {
    if (this.element) {
      this.element.removeEventListener('wheel', this.handleWheel);
      this.element.removeEventListener('dblclick', this.handleDoubleClick);
      this.element.removeEventListener('touchstart', this.handleTouchStart);
      this.element.removeEventListener('mousedown', this.handleMouseDown);
    }

    document.removeEventListener('touchmove', this.handleTouchMove);
    document.removeEventListener('touchend', this.handleTouchEnd);
    document.removeEventListener('mousemove', this.handleMouseMove);
    document.removeEventListener('mouseup', this.handleMouseUp);

    this.stopAnimation();
    this.element = null;
  }

  /**
   * Handle mouse wheel zoom
   */
  private handleWheel(e: WheelEvent): void {
    e.preventDefault();

    const rect = this.element!.getBoundingClientRect();
    const centerX = e.clientX - rect.left;
    const centerY = e.clientY - rect.top;

    // Zoom in or out based on wheel direction
    const delta = e.deltaY > 0 ? -this.config.zoomStep : this.config.zoomStep;
    const newScale = this.clampScale(this.state.scale + delta);

    if (newScale !== this.state.scale) {
      this.zoomTo(newScale, centerX, centerY);
    }
  }

  /**
   * Handle double-click for quick zoom toggle
   */
  private handleDoubleClick(e: MouseEvent): void {
    const rect = this.element!.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    this.toggleQuickZoom(x, y);
  }

  /**
   * Handle touch start - detect pinch or pan
   */
  private handleTouchStart(e: TouchEvent): void {
    if (e.touches.length === 2) {
      // Pinch start
      e.preventDefault();
      this.state.isPinching = true;
      this.lastPinchDistance = this.getPinchDistance(e.touches);
    } else if (e.touches.length === 1) {
      // Check for double-tap
      const touch = e.touches[0];
      const rect = this.element!.getBoundingClientRect();
      const x = touch.clientX - rect.left;
      const y = touch.clientY - rect.top;

      if (this.checkDoubleTap(x, y)) {
        e.preventDefault();
        this.toggleQuickZoom(x, y);
        return;
      }

      // Pan start (only when zoomed)
      if (this.isZoomed()) {
        e.preventDefault();
        this.startPan(touch.clientX, touch.clientY);
      }
    }
  }

  /**
   * Handle touch move - pinch zoom or pan
   */
  private handleTouchMove(e: TouchEvent): void {
    if (this.state.isPinching && e.touches.length === 2) {
      e.preventDefault();

      const newDistance = this.getPinchDistance(e.touches);
      const scaleDelta = newDistance / this.lastPinchDistance;
      const newScale = this.clampScale(this.state.scale * scaleDelta);

      if (newScale !== this.state.scale) {
        // Get element-relative center
        const rect = this.element!.getBoundingClientRect();
        const center = this.getPinchCenter(e.touches);
        const centerX = center.x - rect.left;
        const centerY = center.y - rect.top;

        this.zoomToImmediate(newScale, centerX, centerY);
      }

      this.lastPinchDistance = newDistance;
    } else if (this.state.isPanning && e.touches.length === 1) {
      e.preventDefault();
      this.updatePan(e.touches[0].clientX, e.touches[0].clientY);
    }
  }

  /**
   * Handle touch end
   */
  private handleTouchEnd(e: TouchEvent): void {
    if (this.state.isPinching) {
      this.state.isPinching = false;
      // Update mode after pinch ends
      this.notifyModeChange();
    }

    if (this.state.isPanning && e.touches.length === 0) {
      this.endPan();
    }
  }

  /**
   * Handle mouse down for pan (desktop)
   */
  private handleMouseDown(e: MouseEvent): void {
    // Only start pan if zoomed and not double-click
    if (this.isZoomed()) {
      e.preventDefault();
      this.startPan(e.clientX, e.clientY);
    }
  }

  /**
   * Handle mouse move for pan
   */
  private handleMouseMove(e: MouseEvent): void {
    if (this.state.isPanning) {
      e.preventDefault();
      this.updatePan(e.clientX, e.clientY);
    }
  }

  /**
   * Handle mouse up
   */
  private handleMouseUp(): void {
    if (this.state.isPanning) {
      this.endPan();
    }
  }

  /**
   * Start pan operation
   */
  private startPan(clientX: number, clientY: number): void {
    this.state.isPanning = true;
    this.panStartX = clientX;
    this.panStartY = clientY;
    this.panStartOffsetX = this.state.offsetX;
    this.panStartOffsetY = this.state.offsetY;

    if (this.element) {
      this.element.style.cursor = 'grabbing';
    }
  }

  /**
   * Update pan position
   */
  private updatePan(clientX: number, clientY: number): void {
    const deltaX = clientX - this.panStartX;
    const deltaY = clientY - this.panStartY;

    this.state.offsetX = this.panStartOffsetX + deltaX;
    this.state.offsetY = this.panStartOffsetY + deltaY;

    this.constrainPan();
    this.config.onZoomChange?.(this.state.scale);
  }

  /**
   * End pan operation
   */
  private endPan(): void {
    this.state.isPanning = false;

    if (this.element) {
      this.element.style.cursor = this.isZoomed() ? 'grab' : '';
    }
  }

  /**
   * Zoom to a specific scale with animation
   */
  zoomTo(scale: number, centerX?: number, centerY?: number): void {
    const targetScale = this.clampScale(scale);
    this.animateZoom(targetScale, centerX, centerY);
  }

  /**
   * Zoom immediately without animation (for pinch)
   */
  private zoomToImmediate(scale: number, centerX?: number, centerY?: number): void {
    const oldScale = this.state.scale;
    this.state.scale = scale;

    // Adjust offset to keep zoom centered on point
    if (centerX !== undefined && centerY !== undefined && this.element) {
      const rect = this.element.getBoundingClientRect();
      const canvasCenterX = rect.width / 2;
      const canvasCenterY = rect.height / 2;

      // Calculate new offset to keep point under cursor
      const scaleRatio = scale / oldScale;
      const pointX = centerX - canvasCenterX - this.state.offsetX;
      const pointY = centerY - canvasCenterY - this.state.offsetY;

      this.state.offsetX -= pointX * (scaleRatio - 1);
      this.state.offsetY -= pointY * (scaleRatio - 1);
    }

    // Reset offset when zooming back to 1x
    if (scale === 1) {
      this.state.offsetX = 0;
      this.state.offsetY = 0;
    }

    this.constrainPan();
    this.notifyModeChange();
    this.config.onZoomChange?.(this.state.scale);
  }

  /**
   * Zoom in by one step
   */
  zoomIn(centerX?: number, centerY?: number): void {
    this.zoomTo(this.state.scale + this.config.zoomStep, centerX, centerY);
  }

  /**
   * Zoom out by one step
   */
  zoomOut(centerX?: number, centerY?: number): void {
    this.zoomTo(this.state.scale - this.config.zoomStep, centerX, centerY);
  }

  /**
   * Reset zoom to 1x
   */
  resetZoom(): void {
    this.stopAnimation();
    this.state.scale = 1;
    this.state.offsetX = 0;
    this.state.offsetY = 0;
    this.state.isPanning = false;
    this.state.isPinching = false;
    this.notifyModeChange();
    this.config.onZoomChange?.(this.state.scale);
  }

  /**
   * Toggle between 1x and doubleTapScale
   */
  toggleQuickZoom(x?: number, y?: number): void {
    if (this.state.scale > 1) {
      // Zoom out to 1x
      this.animateZoom(1);
    } else {
      // Zoom in to doubleTapScale
      this.animateZoom(this.config.doubleTapScale, x, y);
    }
  }

  /**
   * Animate zoom transition
   */
  private animateZoom(targetScale: number, centerX?: number, centerY?: number): void {
    this.stopAnimation();

    const startScale = this.state.scale;
    const startOffsetX = this.state.offsetX;
    const startOffsetY = this.state.offsetY;
    const startTime = performance.now();
    const duration = this.config.animationDuration;

    // Calculate target offset
    let targetOffsetX = 0;
    let targetOffsetY = 0;

    if (targetScale > 1 && centerX !== undefined && centerY !== undefined && this.element) {
      const rect = this.element.getBoundingClientRect();
      const canvasCenterX = rect.width / 2;
      const canvasCenterY = rect.height / 2;

      // Offset to center zoom on clicked point
      const scaleRatio = targetScale / startScale;
      const pointX = centerX - canvasCenterX - startOffsetX;
      const pointY = centerY - canvasCenterY - startOffsetY;

      targetOffsetX = startOffsetX - pointX * (scaleRatio - 1);
      targetOffsetY = startOffsetY - pointY * (scaleRatio - 1);
    }

    const animate = (currentTime: number) => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);

      // Ease-out cubic
      const eased = 1 - Math.pow(1 - progress, 3);

      this.state.scale = startScale + (targetScale - startScale) * eased;
      this.state.offsetX = startOffsetX + (targetOffsetX - startOffsetX) * eased;
      this.state.offsetY = startOffsetY + (targetOffsetY - startOffsetY) * eased;

      // Constrain pan every frame to prevent showing areas outside the image
      this.constrainPan();

      this.config.onZoomChange?.(this.state.scale);

      if (progress < 1) {
        this.animationId = requestAnimationFrame(animate);
      } else {
        this.animationId = null;
        this.notifyModeChange();
      }
    };

    this.animationId = requestAnimationFrame(animate);
  }

  /**
   * Stop ongoing animation
   */
  private stopAnimation(): void {
    if (this.animationId !== null) {
      cancelAnimationFrame(this.animationId);
      this.animationId = null;
    }
  }

  /**
   * Constrain pan to keep image within bounds
   */
  private constrainPan(): void {
    if (!this.element || this.state.scale <= 1) {
      this.state.offsetX = 0;
      this.state.offsetY = 0;
      return;
    }

    const rect = this.element.getBoundingClientRect();
    const maxPanX = (rect.width * (this.state.scale - 1)) / 2;
    const maxPanY = (rect.height * (this.state.scale - 1)) / 2;

    this.state.offsetX = Math.max(-maxPanX, Math.min(maxPanX, this.state.offsetX));
    this.state.offsetY = Math.max(-maxPanY, Math.min(maxPanY, this.state.offsetY));
  }

  /**
   * Check for double-tap
   */
  private checkDoubleTap(x: number, y: number): boolean {
    const now = Date.now();
    const timeDiff = now - this.lastTapTime;
    const distance = Math.hypot(x - this.lastTapX, y - this.lastTapY);

    const isDoubleTap = timeDiff < this.DOUBLE_TAP_DELAY && distance < this.DOUBLE_TAP_DISTANCE;

    this.lastTapTime = now;
    this.lastTapX = x;
    this.lastTapY = y;

    return isDoubleTap;
  }

  /**
   * Get distance between two touch points
   */
  private getPinchDistance(touches: TouchList): number {
    const dx = touches[0].clientX - touches[1].clientX;
    const dy = touches[0].clientY - touches[1].clientY;
    return Math.hypot(dx, dy);
  }

  /**
   * Get center point between two touches
   */
  private getPinchCenter(touches: TouchList): { x: number; y: number } {
    return {
      x: (touches[0].clientX + touches[1].clientX) / 2,
      y: (touches[0].clientY + touches[1].clientY) / 2,
    };
  }

  /**
   * Clamp scale to min/max bounds
   */
  private clampScale(scale: number): number {
    return Math.max(this.config.minScale, Math.min(this.config.maxScale, scale));
  }

  /**
   * Notify mode change callback
   */
  private notifyModeChange(): void {
    const mode = this.getInteractionMode();
    this.config.onModeChange?.(mode);

    if (this.element) {
      if (mode === 'pan') {
        this.element.classList.add('is-zoomed');
      } else {
        this.element.classList.remove('is-zoomed');
      }
    }
  }

  // Public getters

  getScale(): number {
    return this.state.scale;
  }

  getOffset(): { x: number; y: number } {
    return { x: this.state.offsetX, y: this.state.offsetY };
  }

  isZoomed(): boolean {
    return this.state.scale > 1;
  }

  getInteractionMode(): 'rotate' | 'pan' {
    return this.state.scale > 1 ? 'pan' : 'rotate';
  }

  getState(): Readonly<ZoomState> {
    return { ...this.state };
  }
}
